import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { EyeSlash, ArrowCounterClockwise, Buildings, MapPin, Clock, ArrowLeft } from "@phosphor-icons/react";
import { postingAge } from "@/lib/format";

const REASON_LABEL = {
  not_relevant: "Non pertinent",
  wrong_role: "Mauvais métier",
  wrong_location: "Mauvaise localisation",
  already_handled: "Déjà traité",
  other: "Autre",
};

export default function HiddenSignals() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hidden-signals");
      setItems(data.hidden || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (signalId) => {
    try {
      await api.delete(`/opportunities/${signalId}/hide`);
      setItems((prev) => prev.filter((i) => i.signal_id !== signalId));
      toast.success("Signal restauré — il réapparaîtra dans vos recherches");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="hidden-signals-page">
      <button onClick={() => navigate("/app")} data-testid="hidden-back-btn"
        className="flex items-center gap-1.5 text-sm text-muted hover:text-fg mb-4">
        <ArrowLeft size={15} /> Retour à la recherche
      </button>
      <div className="flex items-center gap-2 mb-1">
        <EyeSlash size={22} className="text-accent" weight="duotone" />
        <h1 className="font-display text-2xl font-bold">Signaux masqués</h1>
        <span className="text-xs font-mono text-muted bg-surface border border-border rounded-full px-2 py-0.5" data-testid="hidden-count">{items.length}</span>
      </div>
      <p className="text-muted text-sm mb-6">Les signaux que vous avez masqués n'apparaissent plus dans vos recherches. Ce masquage vous est propre — il ne modifie rien pour les autres utilisateurs. Vous pouvez les restaurer à tout moment.</p>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4" data-testid="hidden-loading">
          {[...Array(4)].map((_, i) => <div key={i} className="surface rounded-lg p-5 h-24 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="surface rounded-lg p-10 text-center text-muted" data-testid="hidden-empty">
          Aucun signal masqué. Utilisez « Masquer ce signal » sur une carte d'opportunité pour l'écarter de vos résultats.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="hidden-list">
          {items.map((it) => {
            const op = it.opportunity || {};
            const age = postingAge(it.posted_date || op.posted_at);
            return (
              <div key={it.signal_id} className="surface rounded-lg p-4 flex flex-col gap-2" data-testid={`hidden-item-${it.signal_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-fg truncate">{op.role_title || "—"}</div>
                    <div className="text-sm text-muted flex items-center gap-1 truncate"><Buildings size={13} /> {it.company?.name || "—"}</div>
                  </div>
                  <button onClick={() => restore(it.signal_id)} data-testid={`restore-signal-${it.signal_id}`}
                    className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-accent/15 text-accent border border-accent/30 px-3 py-1.5 rounded-md hover-lift">
                    <ArrowCounterClockwise size={14} /> Restaurer
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted font-mono">
                  <span className="flex items-center gap-1"><MapPin size={12} /> {op.city || "—"}</span>
                  {age && <span className="flex items-center gap-1"><Clock size={12} /> {age}</span>}
                  {it.reason && (
                    <span className="text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5">
                      Motif : {REASON_LABEL[it.reason] || it.reason}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
